import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Printer, CheckCircle, Clock, CreditCard, Envelope, Phone, X } from "@phosphor-icons/react";
import { api, formatUSD } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { toast } from "sonner";
import { buildReceiverPrintableReport } from "./receiverPrintableReport";

/**
 * Drill-down dialog for a single receiver. Opened from the Recent
 * Commissions table when the agent clicks on a receiver's name.
 *
 * Loads /api/agent/receiver?email=… on open and renders:
 *   • Receiver header (name / email / phone)
 *   • KPI tiles (cards count, total balance, funded total, commission)
 *   • Cards grid with image thumbnails
 *   • Commissions table
 *   • Print button → opens the printable report in a new tab
 */
export function ReceiverDetailDialog({ open, onOpenChange, receiverEmail, agentMe }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!receiverEmail) return;
    setLoading(true);
    try {
      const r = await api.get(`/agent/receiver?email=${encodeURIComponent(receiverEmail)}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("agent.receiver.load_failed"));
      onOpenChange?.(false);
    } finally {
      setLoading(false);
    }
  }, [receiverEmail, t, onOpenChange]);

  useEffect(() => {
    if (open) load();
    else setData(null);
  }, [open, load]);

  const print = () => {
    if (!data) return;
    const res = buildReceiverPrintableReport(agentMe, data);
    if (res?.fallback) toast.message(t("agent.commissions.print_popup_blocked"));
  };

  const profile = data?.profile || {};
  const totals = data?.totals || {};
  const cards = data?.cards || [];
  const comms = data?.commissions || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="receiver-detail-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-3xl flex items-center gap-2">
            {loading ? t("agent.receiver.loading") : (profile.receiver_name || profile.receiver_email)}
          </DialogTitle>
          <DialogDescription className="text-[#4A5C50] sr-only">
            {t("agent.receiver.desc")}
          </DialogDescription>
        </DialogHeader>

        {!loading && data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="receiver-profile">
              <ProfileTile icon={<Envelope size={14} weight="duotone" />} label={t("agent.receiver.field.email")} value={profile.receiver_email} />
              <ProfileTile icon={<Phone size={14} weight="duotone" />} label={t("agent.receiver.field.phone")} value={profile.receiver_phone || "—"} />
              <ProfileTile icon={<CheckCircle size={14} weight="duotone" />} label={t("agent.receiver.field.first_seen")} value={totals.first_seen ? new Date(totals.first_seen).toLocaleDateString() : "—"} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="receiver-stats">
              <KpiTile label={t("agent.receiver.kpi.cards")} value={totals.cards_count ?? 0} sub={`${totals.active_cards_count ?? 0} ${t("agent.receiver.kpi.active")}`} />
              <KpiTile label={t("agent.receiver.kpi.balance")} value={formatUSD(totals.balance_total)} highlight="green" />
              <KpiTile label={t("agent.receiver.kpi.funded")} value={formatUSD(totals.transaction_total)} />
              <KpiTile label={t("agent.receiver.kpi.commission")} value={formatUSD(totals.commission_total)} highlight="emerald" />
            </div>

            <h3 className="serif text-xl mt-5">{t("agent.receiver.cards_h", { n: cards.length })}</h3>
            {cards.length === 0 ? (
              <p className="text-sm text-[#4A5C50] mt-1">{t("agent.receiver.no_cards")}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {cards.map((c) => (
                  <ReceiverCard key={c.id} card={c} />
                ))}
              </div>
            )}

            <h3 className="serif text-xl mt-6">{t("agent.receiver.commissions_h", { n: comms.length })}</h3>
            {comms.length === 0 ? (
              <p className="text-sm text-[#4A5C50] mt-1">{t("agent.receiver.no_commissions")}</p>
            ) : (
              <div className="border border-[#E5E0D8] bg-white rounded-xl overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead className="text-left text-[#4A5C50] text-[11px] uppercase tracking-widest">
                    <tr>
                      <th className="p-3">{t("agent.receiver.col_when")}</th>
                      <th className="p-3">{t("agent.receiver.col_type")}</th>
                      <th className="p-3 text-right">{t("agent.receiver.col_amount")}</th>
                      <th className="p-3 text-right">{t("agent.receiver.col_commission")}</th>
                      <th className="p-3">{t("agent.receiver.col_status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comms.map((r) => (
                      <tr key={r.id} className="border-t border-[#E5E0D8]">
                        <td className="p-3 text-xs">{new Date(r.earned_at).toLocaleString()}</td>
                        <td className="p-3 capitalize">{r.transaction_type?.replace("_", " ")}</td>
                        <td className="p-3 text-right mono">{formatUSD(r.transaction_amount)}</td>
                        <td className="p-3 text-right mono font-semibold text-emerald-700">{formatUSD(r.commission_amount)}</td>
                        <td className="p-3">
                          {r.status === "paid"
                            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={12} weight="fill" /> {t("agent.commissions.paid")}</span>
                            : <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Clock size={12} weight="duotone" /> {t("agent.commissions.unpaid")}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-[#E5E0D8]">
              <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="receiver-close-btn">
                <X size={14} className="mr-1" /> {t("agent.receiver.close")}
              </Button>
              <Button type="button" onClick={print} className="bg-[#184A30] text-white" data-testid="receiver-print-btn">
                <Printer size={14} className="mr-1.5" /> {t("agent.receiver.print")}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProfileTile({ icon, label, value }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-[#4A5C50] flex items-center gap-1.5">{icon} {label}</div>
      <div className="mono text-sm mt-1 text-[#112318] break-all">{value || "—"}</div>
    </div>
  );
}

function KpiTile({ label, value, sub, highlight }) {
  const color = highlight === "green" ? "text-[#184A30]"
              : highlight === "emerald" ? "text-emerald-700"
              : "text-[#112318]";
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg p-3 text-center">
      <div className="text-[10px] uppercase tracking-widest text-[#4A5C50]">{label}</div>
      <div className={`serif text-2xl mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-[#4A5C50] mt-1">{sub}</div>}
    </div>
  );
}

function ReceiverCard({ card }) {
  const t = useT();
  const statusClass = card.status === "active" ? "bg-emerald-100 text-emerald-800"
                    : card.status === "pending_payment" ? "bg-amber-100 text-amber-800"
                    : "bg-stone-200 text-stone-700";
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl p-3 flex gap-3" data-testid={`receiver-card-${card.id}`}>
      <div className="shrink-0">
        {card.inline_image_url ? (
          <a href={card.inline_image_url} target="_blank" rel="noreferrer" className="block rounded-md overflow-hidden border border-[#E5E0D8] hover:border-[#184A30]" title={t("agent.receiver.open_full")}>
            <img src={card.inline_image_url} alt="card" className="block" style={{ width: 110, height: 70, objectFit: "cover", background: "#0F3320" }} loading="lazy" />
          </a>
        ) : (
          <div className="rounded-md border border-dashed border-[#C7C0B4] bg-[#FBF9F6] flex items-center justify-center" style={{ width: 110, height: 70 }}>
            <CreditCard size={20} className="text-[#8BA888]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-semibold ${statusClass}`}>{(card.status || "").replace("_", " ")}</span>
          <span className="text-[10px] uppercase tracking-widest text-[#4A5C50]">{(card.kind || "customer").replace("_", " ")}</span>
        </div>
        <div className="mono text-sm mt-1 text-[#112318]">{card.pan_masked || "—"}</div>
        <div className="font-semibold text-[#184A30] mt-0.5">{formatUSD(card.balance_vnd)}</div>
        <div className="text-[10px] text-[#4A5C50] mt-0.5">{new Date(card.created_at).toLocaleString()}</div>
      </div>
    </div>
  );
}
